"use client";

import React, { useState, useEffect, useCallback, useRef } from "react";
import Image from "next/image";
import { CategoryArticlesSidebar } from "./CategoryArticlesSidebar";
import { ArticleAuthors } from "../../partials/ArticleAuthors/ArticleAuthors";
import { ArticleBody } from "../../partials/ArticleBody/ArticleBody";
import { ArticleMatchDetail } from "../../partials/ArticleMatchDetail/ArticleMatchDetail";
import { ArticlePublishedInfo } from "../../partials/shared/ArticlePublishedInfo";
import { useArticleData } from "../../context/ArticleDataContext";
import { useNavigation } from "@/lib/navigation/NavigationProvider";
import type { Post, Media, Category } from "@/payload-types";
import { getMediaUrl } from "@/utilities/media/getMediaUrl";
import { MediaCredit } from "@/components/MediaCredit/MediaCredit";
import { MediaCaption } from "@/components/MediaCaption/MediaCaption";
import { AdSlot } from "@/features/ads/AdSlot";
import type { LocaleCode } from "@/lib/labels/types";
import { ChallengeZone } from "@/features/challenge-zone/ChallengeZone";

/**
 * Update page metadata (title, description, og tags)
 */
function updatePageMetadata(post: Post, tenantName: string) {
    // Update document title
    document.title = `${post.title} | ${tenantName}`;

    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
        metaDescription.setAttribute("content", post.summary || post.title);
    }

    // Update Open Graph tags
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
        ogTitle.setAttribute("content", `${post.title} | ${tenantName}`);
    }

    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
        ogDescription.setAttribute("content", post.summary || post.title);
    }

    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) {
        ogUrl.setAttribute("content", window.location.href);
    }

    // Update hero image in og:image if available — use "og" pre-generated size
    const heroImage = post.heroImage as Media | null;
    const ogImageUrl = getMediaUrl(heroImage, "og") || heroImage?.url;
    if (ogImageUrl) {
        const ogImage = document.querySelector('meta[property="og:image"]');
        if (ogImage) {
            ogImage.setAttribute("content", ogImageUrl);
        }
    }
}

/**
 * Push pageview event
 */
function pushPageview(post: Post, url: string) {
    // Google Analytics 4 (gtag)
    if (typeof window !== "undefined" && "gtag" in window) {
        (window as typeof window & { gtag: (...args: unknown[]) => void }).gtag("event", "page_view", {
            page_title: post.title,
            page_location: url,
            page_path: new URL(url).pathname,
        });
    }

    // Google Tag Manager dataLayer
    if (typeof window !== "undefined" && "dataLayer" in window) {
        (window as typeof window & { dataLayer: unknown[] }).dataLayer.push({
            event: "pageview",
            page: {
                title: post.title,
                url: url,
                path: new URL(url).pathname,
            },
            article: {
                id: post.id,
                title: post.title,
                slug: post.slug,
            },
        });
    }

    // Custom event for any other analytics
    window.dispatchEvent(
        new CustomEvent("article:pageview", {
            detail: {
                postId: post.id,
                title: post.title,
                slug: post.slug,
                url: url,
            },
        })
    );
}

/**
 * Single Article Component - renders one full article
 */
function ArticleContent({
    post,
    category,
    locale,
    isFirst = false,
}: {
    post: Post;
    category: Category | null;
    locale: LocaleCode;
    isFirst?: boolean;
}) {
    const heroImage = post.heroImage as Media | null;
    const TitleTag = isFirst ? "h1" : "h2";

    return (
        <div className="max-w-180 py-8 border-b border-border last:border-b-0">
            {/* Category Badge */}
            {category && (
                <div className="mb-4">
                    <span className="text-xs font-bold text-primary uppercase tracking-wider">{category.title}</span>
                </div>
            )}

            {/* Article Title */}
            <TitleTag className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight mb-4">{post.title}</TitleTag>

            {/* Article Meta */}
            <div className="flex flex-wrap items-center gap-2 text-sm text-foreground/60 mb-6">
                {post.populatedAuthors && post.populatedAuthors.length > 0 && (
                    <>
                        <span className="font-medium text-foreground">
                            {post.populatedAuthors.map((author) => author.name).join(", ")}
                        </span>
                        <span>·</span>
                    </>
                )}
                <ArticlePublishedInfo publishedAt={post.publishedAt} updatedAt={post.updatedAt} locale={locale} />
            </div>

            {/* Hero Image */}
            {heroImage?.url && (
                <div className="mb-8">
                    <div className="relative aspect-video rounded-lg overflow-hidden">
                        <Image
                            src={getMediaUrl(heroImage, "xlarge") || heroImage.url}
                            alt={heroImage.alt || post.title}
                            fill
                            sizes="(max-width: 768px) 100vw, 960px"
                            className="object-cover"
                            priority={isFirst}
                            unoptimized
                        />
                    </div>
                    <MediaCaption media={heroImage} />
                    <MediaCredit media={heroImage} className="mt-1 text-right" />
                </div>
            )}

            {/* Summary */}
            {post.summary && (
                <p className="text-lg md:text-xl text-foreground/80 leading-relaxed mb-8 font-medium">{post.summary}</p>
            )}

            {/* Article Body Content */}
            <ArticleBody content={post.content} />

            {/* Match detail block — shown when post is tagged with a match (first article only) */}
            {isFirst && <ArticleMatchDetail />}

            {/* Authors Footer */}
            <ArticleAuthors authors={post.populatedAuthors} className="mt-8 pt-6" />
        </div>
    );
}

interface ArticleLayoutScrollingProps {
    breadcrumbs?: React.ReactNode;
}

export function ArticleLayoutScrolling({ breadcrumbs }: ArticleLayoutScrollingProps = {}) {
    const { post, categoryPosts, primaryCategory: category, locale: localeStr, tenantName, relatedEntities } = useArticleData();
    const locale = localeStr as LocaleCode;
    const nav = useNavigation();

    // Start with just the current post - load next one when user scrolls
    const [articles, setArticles] = useState<Post[]>([post]);
    const [currentArticleId, setCurrentArticleId] = useState<number>(post.id);
    const [hasMore, setHasMore] = useState(categoryPosts.length > 0);
    const loaderRef = useRef<HTMLDivElement>(null);
    const articleRefs = useRef<Map<number, HTMLElement>>(new Map());
    const contentContainerRef = useRef<HTMLDivElement>(null);
    const viewedArticles = useRef<Set<number>>(new Set([post.id])); // Track which articles have had pageviews

    // Load the next article from categoryPosts (no API fetching)
    const loadNextArticle = useCallback(() => {
        if (!hasMore) return;

        // Get articles from categoryPosts that we haven't shown yet
        const existingIds = new Set(articles.map((a) => a.id));
        const remainingArticles = categoryPosts.filter((p) => !existingIds.has(p.id));

        if (remainingArticles.length > 0) {
            // Add the next article
            setArticles((prev) => [...prev, remainingArticles[0]]);
            // Continue only if there are more articles after this one
            setHasMore(remainingArticles.length > 1);
        } else {
            // No more articles
            setHasMore(false);
        }
    }, [hasMore, articles, categoryPosts]);

    // Intersection Observer for loading next article when reaching the end
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && hasMore) {
                    loadNextArticle();
                }
            },
            { threshold: 0.1 }
        );

        const current = loaderRef.current;
        if (current) {
            observer.observe(current);
        }

        return () => {
            if (current) {
                observer.unobserve(current);
            }
        };
    }, [loadNextArticle, hasMore]);

    // Ref to store the observer so we can add new elements to it
    const observerRef = useRef<IntersectionObserver | null>(null);
    const observedElementsRef = useRef<Set<number>>(new Set());
    // Keep articles in a ref so the observer callback can access the latest values
    const articlesRef = useRef<Post[]>(articles);
    const currentArticleIdRef = useRef<number>(currentArticleId);

    // Update refs when state changes
    useEffect(() => {
        articlesRef.current = articles;
    }, [articles]);

    useEffect(() => {
        currentArticleIdRef.current = currentArticleId;
    }, [currentArticleId]);

    // Track visibility ratios for all observed articles
    const visibilityMapRef = useRef<Map<number, number>>(new Map());

    // Create the intersection observer - must wait for container ref to be available
    useEffect(() => {
        const container = contentContainerRef.current;
        if (!container) return;

        const handleIntersection = (entries: IntersectionObserverEntry[]) => {
            // Update visibility map with current intersection ratios
            for (const entry of entries) {
                const articleId = Number((entry.target as HTMLElement).getAttribute("data-article-id"));
                if (articleId) {
                    if (entry.isIntersecting) {
                        visibilityMapRef.current.set(articleId, entry.intersectionRatio);
                    } else {
                        visibilityMapRef.current.set(articleId, 0);
                    }
                }
            }

            // Find the article with the highest visibility ratio
            let bestArticleId: number | null = null;
            let maxRatio = 0;

            visibilityMapRef.current.forEach((ratio, articleId) => {
                if (ratio > maxRatio) {
                    maxRatio = ratio;
                    bestArticleId = articleId;
                }
            });

            if (!bestArticleId || maxRatio < 0.1) return;
            if (bestArticleId === currentArticleIdRef.current) return;

            // Find the article from our ref
            const article = articlesRef.current.find((a) => a.id === bestArticleId);
            if (!article) return;

            // Update URL - use correct locale prefix based on tenant config
            const newUrl = nav.post(article);
            window.history.pushState({ articleId: bestArticleId }, "", newUrl);

            // Update page metadata
            updatePageMetadata(article, tenantName);

            // Push pageview (only once per article)
            if (!viewedArticles.current.has(bestArticleId)) {
                viewedArticles.current.add(bestArticleId);
                pushPageview(article, window.location.origin + newUrl);
            }

            // Update state
            setCurrentArticleId(bestArticleId);
        };

        observerRef.current = new IntersectionObserver(handleIntersection, {
            root: container, // Use the scrollable container as the root
            threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1.0],
            rootMargin: "-10% 0px -10% 0px",
        });

        // Re-observe all articles with the new observer
        visibilityMapRef.current.clear();
        observedElementsRef.current.clear();
        articles.forEach((article) => {
            const element = articleRefs.current.get(article.id);
            if (element) {
                observerRef.current!.observe(element);
                observedElementsRef.current.add(article.id);
            }
        });

        return () => {
            observerRef.current?.disconnect();
        };
    }, [nav, tenantName, articles]);

    // Observe newly added articles - with a small delay to ensure refs are set
    useEffect(() => {
        const timer = setTimeout(() => {
            if (!observerRef.current) return;

            articles.forEach((article) => {
                if (!observedElementsRef.current.has(article.id)) {
                    const element = articleRefs.current.get(article.id);
                    if (element) {
                        observerRef.current!.observe(element);
                        observedElementsRef.current.add(article.id);
                    }
                }
            });
        }, 100);

        return () => clearTimeout(timer);
    }, [articles]);

    // Handle browser back/forward navigation
    useEffect(() => {
        const handlePopState = (event: PopStateEvent) => {
            if (event.state?.articleId) {
                const articleElement = articleRefs.current.get(event.state.articleId);
                if (articleElement) {
                    articleElement.scrollIntoView({ behavior: "smooth" });
                }
            }
        };

        window.addEventListener("popstate", handlePopState);
        return () => window.removeEventListener("popstate", handlePopState);
    }, []);

    return (
        <div className="h-[calc(100vh-4rem)] bg-background flex overflow-hidden">
            {/* Scrollable Sidebar on left */}
            <CategoryArticlesSidebar initialPost={post} categoryPosts={categoryPosts} activePostId={currentArticleId} />

            {/* Main Content - scrollable independently */}
            <div ref={contentContainerRef} className="flex-1 overflow-y-auto">
                <div className="max-w-4xl mx-auto px-4 md:px-8 space-y-8">
                    {breadcrumbs}
                    <AdSlot id="content-top" />

                    {articles.map((article, index) => (
                        <article
                            key={article.id}
                            ref={(el) => {
                                if (el) articleRefs.current.set(article.id, el);
                            }}
                            data-article-id={article.id}
                        >
                            <ArticleContent post={article} category={category} locale={locale} isFirst={index === 0} />
                        </article>
                    ))}

                    {/* Challenge Zone */}
                    <ChallengeZone relatedEntities={relatedEntities} locale={localeStr} />

                    {/* Trigger to load next article / End message */}
                    <div ref={loaderRef} className="py-8">
                        {!hasMore && articles.length > 1 && (
                            <div className="text-center text-foreground/40 text-sm">You&apos;re all caught up!</div>
                        )}
                    </div>

                    <AdSlot id="content-bottom" />
                </div>
            </div>
        </div>
    );
}
